<template>
    <div class="form-submit-recaptcha" :class="bemFacets">
        <div class="form-submit-recaptcha--wrap">
            <vue-recaptcha
                    badge="inline"
                    :sitekey="sitekey"
                    ref="recaptcha"
                    @render="onRecaptchaRendered($event)"
                    @expired="onRecaptchaExpired($event)"
                    @verify="onRecaptchaVerified($event)">
                <loader-btn class="form-submit-recaptcha--btn"
                        type="submit"
                        :disabled.prop="isDisabled"
                        :btn-facets="btnFacets"
                        :is-loading="isLoading"
                        @click.native="onVerify">
                    <slot/>
                </loader-btn>
            </vue-recaptcha>
        </div>

        <div class="form-submit-recaptcha--notice">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="form-submit-recaptcha--notice-text" v-html="recaptchaDisclaimer"/>
        </div>
    </div>
</template>

<script>
    import VueRecaptcha from 'vue-recaptcha';
    import { mapFilters } from 'util/vue/map-filters';
    import { FORM_SUBMISSION_STATE } from 'util/vue/form';
    import LoaderBtn from 'framework/components/buttons/loader-btn/loader-btn';
    import { isMountedMixin, bemMixin } from '@dreipol/vue-ui/src/mixins';

    export default {
        components: {
            LoaderBtn,
            VueRecaptcha,
        },
        mixins: [
            isMountedMixin,
            bemMixin('form-submit-recaptcha'),
        ],
        props: {
            sitekey: {
                type: String,
                required: true,
            },
            submissionState: {
                type: String,
                required: true,
            },
            recaptchaDisclaimer: {
                type: String,
                required: true,
            },
            onFormSubmit: {
                type: Function,
                required: true,
            },
            btnFacets: {
                type: Array,
                default() {
                    return ['primary', 'medium'];
                },
            },
        },
        data() {
            return {
                isReady: false,
                isVerifying: false,
                isVerified: false,
                widgetId: null,
            };
        },
        computed: {
            isDisabled() {
                return this.isReady ? null : true;
            },
            isLoading() {
                return this.isVerifying || this.submissionState === FORM_SUBMISSION_STATE.SUBMITTING;
            },
        },
        methods: {
            ...mapFilters(['translate']),
            activateRecaptcha() {
                window.vueRecaptchaApiLoaded();
                this.isReady = true;
            },
            resetRecaptcha() {
                // Only reset the captcha when the form actually uses it and form submission was not yet successful
                if (this.submissionState === FORM_SUBMISSION_STATE.SUCCESS) {
                    return;
                }

                this.$refs.recaptcha.reset(this.widgetId);
                this.isVerified = false;
            },
            submitForm() {
                this.isVerifying = false;

                if (!this.isLoading) {
                    this.onFormSubmit();
                }
            },
            onRecaptchaRendered(widgetId) {
                this.$log.debug('Recaptcha is rendered!', widgetId);
                this.widgetId = widgetId;
            },
            onRecaptchaExpired() {
                this.$log.debug('Recaptcha is expired! Resetting...');
                this.resetRecaptcha();
            },
            onRecaptchaVerified(response) {
                this.$log.debug('Recaptcha is verified!', response);
                this.isVerified = true;

                this.submitForm();
            },
            onVerify(event) {
                event.preventDefault();
                event.stopPropagation();

                this.isVerifying = true;

                if (!this.isReady || this.widgetId == null) {
                    throw Error('Recaptcha was not loaded properly at the time the submit was triggered!');
                }

                if (this.isVerified) {
                    this.submitForm();
                }
            },
        },
        watch: {
            submissionState: {
                handler() {
                    if (this.submissionState === FORM_SUBMISSION_STATE.ERROR) {
                        this.onRecaptchaExpired();
                    }
                },
            },
        },
        mounted() {
            if (globals.state.googleRecaptchaLoaded) {
                this.activateRecaptcha();
            } else {
                window.addEventListener('recaptcha-loaded', this.activateRecaptcha);
            }
        },
        beforeDestroy() {
            window.removeEventListener('recaptcha-loaded', this.activateRecaptcha);
        },
    };
</script>
